import React, { useEffect, useState } from "react";
import api, { formatEUR } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { Euro, TrendingUp, TrendingDown, Wallet, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Static chart styles — extracted so React/Recharts don't see new object identities per render.
const CHART_TICK = { fontSize: 11, fill: "#64748b" };
const CHART_TOOLTIP_LABEL = { color: "#0f172a", fontWeight: 600 };
const BAR_CHART_MARGIN = { left: 10 };
const BAR_RADIUS = [0, 6, 6, 0];
const eurTick = (v) => `€${v}`;
const eurFmt = (v) => formatEUR(v);
const dateTick = (d) => d.slice(5);

function Kpi({ label, value, sub, accent = "blue", testid }) {
  const colors = {
    blue: "text-blue-700 bg-blue-50 border-blue-100",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    red: "text-red-700 bg-red-50 border-red-100",
  };
  return (
    <div
      className="kpi-card bg-white border border-slate-200 rounded-xl p-6 md:p-7"
      data-testid={testid}
    >
      <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border ${colors[accent]}`}>
        {label}
      </div>
      <div className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tight text-slate-950">
        {value}
      </div>
      <div className="mt-1 text-sm text-slate-500">{sub}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const canView =
    user?.role === "admin" || user?.permissions?.can_view_reports;
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      const [s, sr, t, sales] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/reports/daily-series", { params: { days: 30 } }),
        api.get("/reports/top-car-types"),
        api.get("/sales"),
      ]);
      setSummary(s.data);
      setSeries(sr.data);
      setTop(t.data);
      setRecent(sales.data.slice(0, 5));
    })();
  }, [canView]);

  if (!canView) return <Navigate to="/venta" replace />;

  const dayProfit = summary?.day?.profit ?? 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-950">
            Panel de control
          </h1>
          <p className="text-slate-500 mt-1">
            Resumen de tus ventas, gastos y ganancias.
          </p>
        </div>
        <Link
          to="/venta"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-md font-medium transition-colors"
          data-testid="dashboard-new-sale-button"
        >
          Registrar venta <ArrowRight className="w-4 h-4" />
        </Link>
      </header>

      {/* KPI row */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        <Kpi
          label="Hoy · Ventas"
          value={formatEUR(summary?.day?.sales || 0)}
          sub={`${summary?.day?.sales_count || 0} servicios`}
          accent="blue"
          testid="kpi-day-sales"
        />
        <Kpi
          label="Hoy · Gastos"
          value={formatEUR(summary?.day?.expenses || 0)}
          sub={`${summary?.day?.expenses_count || 0} movimientos`}
          accent="red"
          testid="kpi-day-expenses"
        />
        <Kpi
          label="Hoy · Ganancia"
          value={formatEUR(dayProfit)}
          sub={dayProfit >= 0 ? "Resultado positivo" : "En negativo"}
          accent={dayProfit >= 0 ? "emerald" : "red"}
          testid="kpi-day-profit"
        />
        <Kpi
          label="Mes · Ganancia"
          value={formatEUR(summary?.month?.profit || 0)}
          sub={`Ventas ${formatEUR(summary?.month?.sales || 0)}`}
          accent="emerald"
          testid="kpi-month-profit"
        />
      </section>

      {/* Period summary table-like */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {[
          { key: "day", label: "Hoy" },
          { key: "week", label: "Esta semana" },
          { key: "month", label: "Este mes" },
        ].map(({ key, label }) => {
          const p = summary?.[key] || { sales: 0, expenses: 0, profit: 0 };
          return (
            <div
              key={key}
              className="bg-white border border-slate-200 rounded-xl p-6"
              data-testid={`period-card-${key}`}
            >
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {label}
              </div>
              <div className="mt-4 space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-slate-600">
                    <TrendingUp className="w-4 h-4 text-blue-600" /> Ventas
                  </span>
                  <span className="font-semibold tabular-nums">
                    {formatEUR(p.sales)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-slate-600">
                    <TrendingDown className="w-4 h-4 text-red-500" /> Gastos
                  </span>
                  <span className="font-semibold tabular-nums">
                    {formatEUR(p.expenses)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 mt-2">
                  <span className="flex items-center gap-2 text-slate-900 font-medium">
                    <Wallet className="w-4 h-4 text-emerald-600" /> Ganancia
                  </span>
                  <span
                    className={`font-display text-xl font-bold tabular-nums ${
                      p.profit >= 0 ? "text-emerald-700" : "text-red-600"
                    }`}
                  >
                    {formatEUR(p.profit)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg">
              Ventas vs Gastos · últimos 30 días
            </h3>
            <Euro className="w-4 h-4 text-slate-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tick={CHART_TICK}
                  tickFormatter={dateTick}
                />
                <YAxis
                  tick={CHART_TICK}
                  tickFormatter={eurTick}
                />
                <Tooltip
                  formatter={eurFmt}
                  labelStyle={CHART_TOOLTIP_LABEL}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="sales"
                  name="Ventas"
                  stroke="#2563eb"
                  strokeWidth={2}
                  fill="url(#gSales)"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  name="Gastos"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fill="url(#gExp)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-lg mb-4">
            Top por categoría
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top} layout="vertical" margin={BAR_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={CHART_TICK} tickFormatter={eurTick} />
                <YAxis type="category" dataKey="name" tick={CHART_TICK} width={90} />
                <Tooltip formatter={eurFmt} />
                <Bar dataKey="revenue" name="Ingresos" fill="#2563eb" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Recent */}
      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">
            Últimas ventas
          </h3>
          <Link to="/historial" className="text-sm text-blue-600 hover:text-blue-700">
            Ver todo →
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-slate-500 text-sm">Aún no hay ventas registradas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Auto</th>
                  <th className="py-2 pr-4">Extras</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`recent-sale-${s.id}`}>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {new Date(s.date).toLocaleString("es-ES")}
                    </td>
                    <td className="py-2.5 pr-4 font-medium">{s.car_type_name}</td>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {s.extras?.length ? s.extras.map((e) => e.name).join(", ") : "—"}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-600">{s.customer_name || "—"}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold tabular-nums">
                      {formatEUR(s.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
